import { Loader } from 'lucide-react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentLogoMarquee } from './agent-logo-marquee';

/**
 * The wait before a project's agents appear.
 *
 * Extracted on its second consumer, per the repo's promote-on-second-consumer
 * norm: the legacy service list and the redesigned project workspace are the
 * same wait on the same data, and they must not drift into two treatments
 * across a flag flip. Before this, routing the /services index to /agents left
 * the marquee reachable only with the flag off — the new treatment stranded on
 * the old page while the redesign showed the facade LoadingArea.
 *
 * Here rather than in pages/project-overview/ because the legacy page still
 * renders this, and having it import from the redesign's directory would invert
 * the deletion order: service-list dies at flag removal, project-overview
 * outlives it. The marquee moved with it.
 */
export const AgentsLoading: FC = () => {
  const { t } = useTranslation();

  return (
    /* The frame centres this block in the content region rather than offsetting
       it from the top, so the strip and the label sit together as one
       composition. pt-24 is gone with LoadingArea: it was that component's own
       top offset, not a gap the design asks for. */
    <div className="flex flex-1 flex-col items-center justify-center gap-4 py-24">
      <AgentLogoMarquee />
      <div className="flex items-center gap-2" role="status">
        {/* Not motion-safe:animate-spin. A frozen loading indicator reads as
            broken, and under `reduce` this is the only motion left — the strip
            stops. The design system freezes its own spinner in
            DocumentViewer.css; monoweb deliberately does not, and
            project-list-loading.tsx asserts as much. */}
        <Loader
          aria-hidden
          className="animate-spin text-[var(--ds-color-text-secondary)]"
          size={24}
          strokeWidth={1.75}
        />
        {/* The frame's `heading 5` is 18px/22px weight 500, but the shipped
            --ds-typography-heading-5-* tokens are 16px/20px weight 400: the
            Figma style and the exported token have drifted, so there is no token
            that matches the design and switching to one would quietly make this
            wrong. Hence the arbitrary 22px in a file otherwise on tokens. Not
            yet raised upstream. Token drift only — nothing to do with the
            font family, which this inherits: Switzer inside the app shell,
            Inter on the legacy service list this is also rendered from.

            The Loader props above are shared with the LoadingLabel in
            project-list-loading.tsx, but the label deliberately is not: this
            frame is 18px/22px on --text-secondary, that one 16px/24px on
            --text-primary. Extracting a shared component would need props for
            size, weight and colour to express a difference the two frames
            genuinely have, leaving only the icon shared — so the
            near-duplication is deliberate. */}
        <span className="text-lg font-medium leading-[22px] text-[var(--ds-color-text-secondary)]">
          {t('service-list.loading-title')}
        </span>
      </div>
    </div>
  );
};
